/**
 * Loads Mercado Pago's browser SDK.
 *
 * PORTED VERBATIM from Gestionalo (apps/portal/src/utils/loadMercadoPagoSdk.ts,
 * spec c086002b D5) for Fiscalo spec 3f56da7d. Only the script tag's data
 * attribute is renamed. Every comment below records a failure mode that cost
 * that integration real debugging time, so keep them with the code.
 *
 * One in-flight promise, one script tag, and a
 * reset on failure so a transient network error does not poison every later
 * attempt.
 *
 * WHY THE SDK AND NOT OUR OWN CARD FIELDS. The card number never touches our
 * DOM — Payment Brick renders Mercado Pago's own iframes and hands back a
 * single-use token, which is what keeps us at PCI SAQ A. It also supplies the
 * DEVICE FINGERPRINT automatically, which is Mercado Pago's strongest fraud
 * signal and 11 points of their integration-quality score, earned with no code
 * of ours. Do not try to set the device id by hand.
 */

/**
 * What the Payment Brick hands back in `onSubmit`'s INNER `formData`.
 *
 * Only the two fields we forward. The callback's argument is
 * `{ selectedPaymentMethod, formData }` and it is the inner object that
 * carries these — forwarding the outer one makes Mercado Pago answer
 * `required_properties`.
 */
export type MercadoPagoBrickFormData = {
  /** Single-use card token. The card itself never reaches our JS. */
  token?: string;
  payer?: { email?: string };
  /** MP's own method id — 'visa', 'debvisa'. The Orders API needs it. */
  payment_method_id?: string;
  /** How many instalments the payer chose. Annual only. */
  installments?: number;
};

/** Only the surface we actually call, so a change upstream is a type error. */
export type MercadoPagoBrickController = {
  unmount: () => void;
};

export type MercadoPagoBricksBuilder = {
  create: (
    brick: "payment",
    containerId: string,
    settings: unknown,
  ) => Promise<MercadoPagoBrickController>;
};

export type MercadoPagoSdk = {
  bricks: () => MercadoPagoBricksBuilder;
};

type MercadoPagoConstructor = new (
  publicKey: string,
  options?: { locale?: string },
) => MercadoPagoSdk;

declare global {
  interface Window {
    MercadoPago?: MercadoPagoConstructor;
  }
}

const SDK_URL = "https://sdk.mercadopago.com/js/v2";

/**
 * A stalled request fires neither `load` nor `error` — a captive portal, a
 * hung CDN connection, an extension that blocks without failing. Without a
 * deadline the promise would stay memoised forever and the payment page would
 * spin with no way out, so the wait is bounded and a timeout is reported as a
 * failure the caller can retry.
 */
const SDK_LOAD_TIMEOUT_MS = 20_000;

/**
 * The in-flight load, memoised so concurrent callers share one script tag.
 * Single-writer by construction: this module only ever runs on the browser's
 * one JavaScript thread, so the check-then-assign below cannot interleave.
 *
 */
let loadPromise: Promise<MercadoPagoConstructor> | null = null;

/**
 * Which attempt is current. Bumped on every failure, so a LATE event from an
 * abandoned attempt can recognise itself as stale and do nothing.
 *
 * THE BUG THIS EXISTS TO STOP, which the original port had: when attempt 1
 * times out, `fail` nulls `loadPromise` and removes the tag — but the `load`
 * and `error` listeners it registered survive on the now-detached script. A
 * late event from attempt 1 would then re-enter `fail` and remove EVERY
 * `script[data-fiscalo-mercadopago]` in the document, including the fresh tag
 * attempt 2 is waiting on, while nulling attempt 2's `loadPromise`. The result
 * is a promise that neither resolves nor rejects and a payment page that spins
 * forever — precisely the failure the timeout was added to prevent.
 */
let loadGeneration = 0;

export function loadMercadoPagoSdk(): Promise<MercadoPagoConstructor> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Mercado Pago can only load in the browser"));
  }
  if (window.MercadoPago) {
    return Promise.resolve(window.MercadoPago);
  }
  if (loadPromise) {
    return loadPromise;
  }

  const generation = ++loadGeneration;

  loadPromise = new Promise((resolve, reject) => {
    // Declared below, next to the setTimeout that produces it: `settled`,
    // `fail` and the timer are mutually referential (the timer fires `fail`,
    // `fail` calls `settled`, `settled` clears the timer), and nothing here
    // runs until the setTimeout line, so the closure reads it safely.
    const settled = (): void => {
      clearTimeout(timer);
    };
    /** True once a newer attempt has started — see loadGeneration. */
    const isStale = (): boolean => generation !== loadGeneration;
    const fail = (): void => {
      settled();
      // A late event from an abandoned attempt must not touch the tag or the
      // promise a newer attempt is using. It still rejects ITS OWN promise,
      // which nobody is waiting on any more.
      if (isStale()) {
        reject(new Error("Mercado Pago SDK failed to load"));
        return;
      }
      // Cleared so a retry can try again rather than inheriting this failure,
      // and the generation moves so this attempt's surviving listeners go
      // stale rather than sabotaging the next one.
      loadPromise = null;
      loadGeneration += 1;
      /**
       * AND THE DEAD TAG GOES WITH IT. A script element that has already fired
       * its terminal `error` never fires another event, so leaving it in the
       * DOM would make the next attempt take the `existing` branch below and
       * wait forever on listeners that can no longer fire — a promise that
       * neither resolves nor rejects, which is worse than the failure it came
       * from.
       */
      document
        .querySelectorAll('script[data-fiscalo-mercadopago="1"]')
        .forEach((el) => el.remove());
      reject(new Error("Mercado Pago SDK failed to load"));
    };
    const finish = (): void => {
      if (window.MercadoPago) {
        settled();
        // Resolving is safe even when stale: the SDK really did load, and the
        // global is shared, so a newer attempt gets the same constructor.
        resolve(window.MercadoPago);
        return;
      }
      // Loaded but exported nothing we can use — a failure rather than
      // resolving into a null constructor the caller would crash on.
      fail();
    };

    const timer = setTimeout(fail, SDK_LOAD_TIMEOUT_MS);

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-fiscalo-mercadopago="1"]',
    );
    if (existing) {
      if (window.MercadoPago) {
        settled();
        resolve(window.MercadoPago);
        return;
      }
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.dataset.fiscaloMercadopago = "1";
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", fail, { once: true });
    document.head.appendChild(script);
  });

  return loadPromise;
}
